import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Eye,
  Lock,
  Sparkles,
  Scale,
  SunMedium,
  MessagesSquare,
  Loader2,
  type LucideIcon,
} from "lucide-react";
import { Page } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { navigateToMentorWithState } from "@/lib/chatNavigation";
import { snapshotToTeamsCoachingSeed } from "@/lib/myTeamCoachingSeed";
import { MessageCircle } from "lucide-react";
import {
  SAMPLE_SCHOOL_TEAM_SNAPSHOT,
  buildTeamInsightsPreviewSnapshot,
  type FocusArea,
  type FocusAccent,
  type Observation,
  type ObservationDirection,
  type ObservationToneId,
  type SchoolTeamSnapshot,
} from "@/config/teamsSample";
import { getSchoolTeamSnapshot } from "@/services/teamsService";
import { InstitutionalContextDocumentsCard } from "./InstitutionalContextDocumentsCard";

interface SchoolTeamPageProps {
  /** When provided, fetches that school's snapshot; otherwise the sample is shown. */
  schoolId?: string | null;
  /** Optional back-link rendered in the header (e.g. for district drill-down). */
  backTo?: { to: string; label: string };
  canManageOrganizationContext: boolean;
}

function useSchoolTeamSnapshot(schoolId: string | null) {
  const [snapshot, setSnapshot] = useState<SchoolTeamSnapshot>(SAMPLE_SCHOOL_TEAM_SNAPSHOT);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    void getSchoolTeamSnapshot(schoolId)
      .then((next) => {
        if (!cancelled) setSnapshot(next);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  return { snapshot, isLoading };
}

export const SchoolTeamPage = ({ schoolId, backTo, canManageOrganizationContext }: SchoolTeamPageProps) => {
  const sid = schoolId ?? null;
  const { snapshot, isLoading } = useSchoolTeamSnapshot(sid);

  return (
    <Page.Container>
      <Page.Header className="space-y-2 pb-5 md:pb-8">
        {backTo && (
          <Link
            to={backTo.to}
            className="inline-block font-inter text-xs text-white/70 hover:text-white transition-colors"
          >
            ← {backTo.label}
          </Link>
        )}
        <h1 className="font-literata text-3xl font-semibold text-white md:text-4xl">My Team</h1>
        <p className="max-w-3xl font-inter text-sm leading-relaxed text-white/80 md:text-base">
          <span className="font-medium text-white/95">{snapshot.schoolName}</span>
          <span className="mx-2 text-white/50">·</span>
          <span className="italic">{snapshot.tagline}</span>
        </p>
        <SemesterPill semesterLabel={snapshot.semesterLabel} helperCount={snapshot.helperCount} />
      </Page.Header>

      <Page.Content className="gap-0 space-y-0 p-0 md:p-0">
        <div className="rounded-3xl bg-[#faf6ee] px-5 py-6 shadow-xl shadow-black/10 md:px-10 md:py-10">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-stone-400" aria-hidden />
            </div>
          ) : (
            <SchoolReport snapshot={snapshot} canManageOrganizationContext={canManageOrganizationContext} />
          )}
        </div>
      </Page.Content>
    </Page.Container>
  );
};

/** Same report body as the full school team page, for embedding (e.g. district accordion rows). */
export function SchoolTeamEmbeddedContent({ schoolId }: { schoolId: string | null }) {
  const { snapshot, isLoading } = useSchoolTeamSnapshot(schoolId);

  return (
    <div className="rounded-3xl bg-[#faf6ee] px-5 py-6 shadow-xl shadow-black/10 md:px-10 md:py-10">
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-stone-400" aria-hidden />
        </div>
      ) : (
        <SchoolReport snapshot={snapshot} canManageOrganizationContext={false} />
      )}
    </div>
  );
}

// ---------- Report body ----------

const SchoolReport = ({ snapshot, canManageOrganizationContext }: { snapshot: SchoolTeamSnapshot; canManageOrganizationContext: boolean }) => {
  const navigate = useNavigate();
  const hasAggregateSignal = snapshot.helperCount > 0 && snapshot.focusAreas.length > 0;

  const handleTalkItThrough = () => {
    navigateToMentorWithState(navigate, {
      entry: "get_coaching",
      teamsCoachingSeed: snapshotToTeamsCoachingSeed(snapshot),
    });
  };

  const handleThinkThroughObservation = (observation: Observation) => {
    navigateToMentorWithState(navigate, {
      entry: "get_coaching",
      teamsCoachingSeed: snapshotToTeamsCoachingSeed(snapshot, { focusObservation: observation }),
    });
  };

  return (
    <article className="space-y-8">
      <PrivacyBanner />

      {!hasAggregateSignal ? (
        <>
          <TeamInsightsEmptyState helperCount={snapshot.helperCount} />
          <TeamInsightsPreviewSection schoolName={snapshot.schoolName} schoolId={snapshot.schoolId} />
        </>
      ) : (
        <>
          <FocusAreasSection focusAreas={snapshot.focusAreas} />

          <ObservationsSection
            observations={snapshot.observations}
            onThinkThroughObservation={handleThinkThroughObservation}
          />
        </>
      )}

      <TalkItThroughCard onClick={handleTalkItThrough} disabled={!hasAggregateSignal} />

      <InstitutionalContextDocumentsCard
        variant="school"
        scopeKey={snapshot.schoolId}
        entityLabel={snapshot.schoolName}
        canManage={canManageOrganizationContext}
      />
    </article>
  );
};

// ---------- Semester pill (header — matches Insights / My District titling) ----------

const SemesterPill = ({ semesterLabel, helperCount }: { semesterLabel: string; helperCount: number }) => (
  <div className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 py-1 font-inter text-xs text-white/90">
    <span aria-hidden className="h-2 w-2 rounded-full bg-emerald-400" />
    <span>{semesterLabel}</span>
    <span aria-hidden className="text-white/40">
      ·
    </span>
    <span>
      {helperCount} {helperCount === 1 ? "helper" : "helpers"}
    </span>
  </div>
);

// ---------- Privacy banner ----------

const PrivacyBanner = () => (
  <div className="flex items-start gap-3 rounded-2xl border border-amber-200/70 bg-amber-50/70 px-4 py-3 font-inter text-xs leading-relaxed text-stone-700 md:text-sm">
    <Lock className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" aria-hidden />
    <p>
      <span className="font-semibold">Aggregate patterns only.</span> Individual sessions and conversations are
      never visible here. Categories only surface when at least 25% of your staff shows a pattern. This is
      named, not buried in a policy.
    </p>
  </div>
);

const TeamInsightsEmptyState = ({ helperCount }: { helperCount: number }) => (
  <div className="rounded-2xl border border-stone-200 bg-white px-5 py-8 text-center md:px-8">
    <SectionEyebrow eyebrow="Building insights" title="Patterns will appear as your staff uses HeyBodhi" />
    <p className="mx-auto mt-3 max-w-xl font-inter text-sm leading-relaxed text-stone-600 md:text-base">
      {helperCount > 0
        ? `${helperCount} staff member${helperCount === 1 ? " has" : "s have"} started conversations, but no category has reached the 25% threshold yet. Check back as usage grows — nothing here names anyone individually.`
        : "When staff at your building chat with HeyBodhi, aggregate focus areas and observations will show here. Individual sessions stay private."}
    </p>
  </div>
);

const TeamInsightsPreviewSection = ({
  schoolName,
  schoolId,
}: {
  schoolName: string;
  schoolId: string;
}) => {
  const navigate = useNavigate();
  const preview = buildTeamInsightsPreviewSnapshot(schoolName, schoolId);

  const handlePreviewTalkItThrough = () => {
    navigateToMentorWithState(navigate, {
      entry: "get_coaching",
      teamsCoachingSeed: snapshotToTeamsCoachingSeed(preview),
    });
  };

  const handlePreviewObservation = (observation: Observation) => {
    navigateToMentorWithState(navigate, {
      entry: "get_coaching",
      teamsCoachingSeed: snapshotToTeamsCoachingSeed(preview, { focusObservation: observation }),
    });
  };

  return (
    <section
      className="relative space-y-6 rounded-2xl border-2 border-dashed border-stone-300/90 bg-stone-50/50 px-4 py-6 md:px-6 md:py-8"
      aria-labelledby="team-insights-preview-heading"
    >
      <div className="flex items-start gap-3 rounded-xl border border-violet-200/80 bg-violet-50/90 px-4 py-3">
        <Eye className="mt-0.5 h-4 w-4 shrink-0 text-violet-700" aria-hidden />
        <div className="space-y-1">
          <p
            id="team-insights-preview-heading"
            className="font-inter text-xs font-semibold uppercase tracking-[0.14em] text-violet-800 md:text-[11px]"
          >
            Example preview
          </p>
          <p className="font-inter text-sm leading-relaxed text-violet-950/90 md:text-[15px]">
            This is what My Team will look like once enough staff usage is collected in your building.
            Numbers and observations below are illustrative — not your school&apos;s data.
          </p>
        </div>
      </div>

      <FocusAreasSection focusAreas={preview.focusAreas} />

      <ObservationsSection
        observations={preview.observations}
        onThinkThroughObservation={handlePreviewObservation}
      />

      <TalkItThroughCard
        onClick={handlePreviewTalkItThrough}
        buttonLabel="Try example coaching context"
        description="Opens My Coach with the sample building snapshot above — safe to explore."
      />
    </section>
  );
};

// ---------- Focus areas ----------

const FocusAreasSection = ({ focusAreas }: { focusAreas: FocusArea[] }) => (
  <section className="space-y-4">
    <SectionEyebrow eyebrow="Where your staff is going" title="What your staff is reaching for this semester" />
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
      {focusAreas.map((area) => (
        <FocusAreaCard key={area.id} area={area} />
      ))}
    </div>
  </section>
);

const ACCENT_META: Record<
  FocusAccent,
  { icon: LucideIcon; bg: string; text: string; bar: string }
> = {
  self_regulation: {
    icon: Sparkles,
    bg: "bg-emerald-100",
    text: "text-emerald-700",
    bar: "bg-emerald-700",
  },
  conversation_lab: {
    icon: MessagesSquare,
    bg: "bg-amber-100",
    text: "text-amber-800",
    bar: "bg-emerald-700",
  },
  restoration: {
    icon: Scale,
    bg: "bg-teal-100",
    text: "text-teal-800",
    bar: "bg-emerald-700",
  },
  growth: {
    icon: SunMedium,
    bg: "bg-rose-100",
    text: "text-rose-700",
    bar: "bg-emerald-700",
  },
};

const FocusAreaCard = ({ area }: { area: FocusArea }) => {
  const meta = ACCENT_META[area.accent];
  const Icon = meta.icon;
  const pct = Math.max(0, Math.min(100, area.percent));

  return (
    <div className="flex items-center gap-3 rounded-2xl border border-stone-200 bg-white px-4 py-3 md:gap-4 md:px-5 md:py-4">
      <div
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg md:h-10 md:w-10",
          meta.bg,
          meta.text
        )}
        aria-hidden
      >
        <Icon className="h-4 w-4 md:h-5 md:w-5" />
      </div>

      <div className="flex-1 min-w-0">
        <p className="font-literata text-sm font-semibold text-stone-900 md:text-base">{area.label}</p>
        <p className="font-inter text-xs text-stone-500 md:text-sm">{area.category}</p>
      </div>

      <div className="hidden w-24 md:block">
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-stone-200">
          <div className={cn("h-full rounded-full", meta.bar)} style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="ml-2 font-literata text-sm font-semibold text-stone-900 tabular-nums md:text-base">
        {pct}%
      </div>
    </div>
  );
};

// ---------- Observations ----------

const ObservationsSection = ({
  observations,
  onThinkThroughObservation,
}: {
  observations: Observation[];
  onThinkThroughObservation: (observation: Observation) => void;
}) => (
  <section className="space-y-4">
    <SectionEyebrow eyebrow="Observations · This month" title="What's worth noticing" />
    <ul className="space-y-3 md:space-y-4">
      {observations.map((obs) => (
        <li key={obs.id}>
          <ObservationCard
            observation={obs}
            onThinkItThrough={() => onThinkThroughObservation(obs)}
          />
        </li>
      ))}
    </ul>
  </section>
);

const DIRECTION_META: Record<ObservationDirection, { icon: LucideIcon; ariaLabel: string }> = {
  up: { icon: ArrowUpRight, ariaLabel: "Trending up" },
  right: { icon: ArrowRight, ariaLabel: "Worth exploring" },
  down: { icon: ArrowUpRight, ariaLabel: "Trending down" },
};

const ObservationCard = ({
  observation,
  onThinkItThrough,
}: {
  observation: Observation;
  onThinkItThrough: () => void;
}) => {
  const meta = DIRECTION_META[observation.direction];
  const Icon = meta.icon;
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-stone-200 bg-white px-4 py-4 md:gap-4 md:px-5 md:py-5">
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-600 md:h-9 md:w-9"
        aria-label={meta.ariaLabel}
      >
        <Icon className={cn("h-4 w-4 md:h-5 md:w-5", observation.direction === "down" && "rotate-180")} />
      </div>

      <div className="flex-1 min-w-0 space-y-2">
        <p className="font-inter text-sm leading-relaxed text-stone-700 md:text-[15px]">{observation.body}</p>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1.5">
            {observation.tags.map((tag) => (
              <ObservationPill key={tag.label} label={tag.label} tone={tag.tone} />
            ))}
          </div>
          <button
            type="button"
            onClick={onThinkItThrough}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border border-emerald-700/30 bg-emerald-700/5 px-3 py-1 font-inter text-[11px] font-semibold text-emerald-800 transition",
              "hover:border-emerald-700/60 hover:bg-emerald-700/10 md:text-xs"
            )}
            aria-label="Think this observation through with HeyBodhi"
          >
            <MessageCircle className="h-3 w-3 md:h-3.5 md:w-3.5" aria-hidden />
            <span>Think it through</span>
            <ArrowRight className="h-3 w-3 md:h-3.5 md:w-3.5" aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
};

const TONE_CLASSES: Record<ObservationToneId, string> = {
  positive: "bg-emerald-100 text-emerald-800 border-emerald-200",
  worth_exploring: "bg-rose-100 text-rose-800 border-rose-200",
  neutral: "bg-amber-100 text-amber-800 border-amber-200",
};

const ObservationPill = ({ label, tone }: { label: string; tone: ObservationToneId }) => (
  <span
    className={cn(
      "inline-flex items-center rounded-md border px-2 py-0.5 font-inter text-[10px] font-semibold uppercase tracking-wider md:text-[11px]",
      TONE_CLASSES[tone]
    )}
  >
    {label}
  </span>
);

// ---------- Talk-it-through CTA ----------

const TalkItThroughCard = ({
  onClick,
  disabled = false,
  buttonLabel = "Talk it through",
  description = "Opens a coaching session with context from what you're seeing",
}: {
  onClick: () => void;
  disabled?: boolean;
  buttonLabel?: string;
  description?: string;
}) => (
  <div className="rounded-2xl bg-[#1f3a30] px-5 py-5 md:flex md:items-center md:justify-between md:gap-6 md:px-7 md:py-6">
    <div className="flex-1 space-y-2">
      <p className="font-inter text-[10px] font-semibold uppercase tracking-[0.18em] text-white/60 md:text-[11px]">
        Think it through with HeyBodhi
      </p>
      <h3 className="font-literata text-base text-white md:text-lg">
        <span className="font-semibold">Your staff is telling you something. </span>
        <span className="italic text-amber-300">Want to think through what it means — and how you might respond?</span>
      </h3>
      <p className="font-inter text-xs text-white/70 md:text-sm">{description}</p>
    </div>

    <div className="mt-4 md:mt-0 md:shrink-0">
      <Button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="w-full bg-amber-300/90 text-stone-900 hover:bg-amber-200 md:w-auto disabled:opacity-50"
      >
        {buttonLabel} <ArrowRight className="ml-1 h-4 w-4" aria-hidden />
      </Button>
    </div>
  </div>
);

// ---------- Section heading shared ----------

const SectionEyebrow = ({ eyebrow, title }: { eyebrow: string; title: string }) => (
  <div className="space-y-1">
    <p className="font-inter text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-400 md:text-[11px]">
      {eyebrow}
    </p>
    <h2 className="font-literata text-lg font-semibold text-stone-900 md:text-xl">{title}</h2>
  </div>
);
